import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import SecurityLogo from '../images/3eaf6a7b-7862-4992-8651-721d8e1b0e3e.png';
import PasswordResetShell, { PasswordResetLoader } from '../components/PasswordResetShell';
import { AppVarsContext } from '../context/AppVarsContext';
import { IMAGER_APP_ID, passwordLoginUrl } from '../password-links';

const PASSWORD_MIN_LENGTH = 8;

const PasswordReset = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setErrors] = useState({ show: false, msg: '', severity: 'info' });
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token')?.trim() || '');
  const [tokenState, setTokenState] = useState(token ? 'ready' : 'missing');
  const [completedLoginUrl, setCompletedLoginUrl] = useState('');
  const { appVars, appID } = useContext(AppVarsContext);
  const { uid: id } = useParams();
  useEffect(() => {
    if (!completedLoginUrl || appID !== IMAGER_APP_ID) return undefined;
    const redirect = window.setTimeout(() => window.location.assign(completedLoginUrl), 2500);
    return () => window.clearTimeout(redirect);
  }, [completedLoginUrl, appID]);

  useEffect(() => {
    if (token) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  const theme = useMemo(() => {
    if (!appVars?.theme) {
      return createTheme();
    }

    try {
      return createTheme(JSON.parse(appVars.theme));
    } catch (parseError) {
      console.error('Unable to parse application theme:', parseError);
      return createTheme();
    }
  }, [appVars?.theme]);

  useEffect(() => {
    if (!appVars?.application) {
      return;
    }

    TagManager.initialize({
      gtmId: 'GTM-PQ6FN6NX',
      events: {
        passwordReset: 'password-reset',
      },
      dataLayer: {
        userId: id,
        userProject: appID,
        page: appVars.application,
      },
    });
  }, [appID, appVars?.application, id]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!password.trim()) {
      setErrors({
        show: true,
        msg: 'Enter a new password before continuing.',
        severity: 'error',
      });
      return;
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      setErrors({
        show: true,
        msg: `Use at least ${PASSWORD_MIN_LENGTH} characters for your new password.`,
        severity: 'error',
      });
      return;
    }

    if (password !== confirmPassword) {
      setErrors({ show: true, msg: "Passwords don't match", severity: 'error' });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${appVars.api_url}/identity/password-reset/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: id,
          applicationId: appID,
          token,
          password,
        }),
      });

      const result = await response.json().catch(() => ({}));
      const compatibility = result.compatibility || result.error?.compatibility;
      if (
        !response.ok &&
        compatibility?.legacyPasswordReset === true &&
        compatibility?.endpoint === '/resetpassword'
      ) {
        const legacyResponse = await fetch(`${appVars.api_url}/resetpassword`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userid: id, password }),
        });
        const legacyResult = await legacyResponse.json().catch(() => ({}));
        if (!legacyResponse.ok || legacyResult.status === 'error') {
          throw new Error(legacyResult.msg || 'Password reset failed.');
        }
        setTokenState('completed');
        setErrors({ show: true, msg: legacyResult.msg || 'Password has been reset successfully.', severity: 'success' });
        setPassword('');
        setConfirmPassword('');
        return;
      }
      const isErrorStatus =
        !response.ok ||
        result.status === 'error' ||
        (typeof result.status === 'number' && result.status >= 400);

      if (isErrorStatus) {
        const errorCode = result.error?.code || result.code;
        if (errorCode === 'invalid_reset_token') {
          setTokenState('invalid');
          setErrors({ show: false, msg: '', severity: 'info' });
          return;
        }
        throw new Error(result.error?.message || result.msg || 'Password reset failed.');
      }

      setTokenState('completed');
      setCompletedLoginUrl(passwordLoginUrl(appID, appVars, result.data?.loginUrl));
      setErrors({
        show: true,
        msg: result.msg || 'Password has been reset successfully.',
        severity: 'success',
      });
      setPassword('');
      setConfirmPassword('');

    } catch (submitError) {
      console.error('Error:', submitError);
      setErrors({
        show: true,
        msg: submitError.message || 'An error occurred while resetting the password.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setErrors({ show: false, msg: '', severity: 'info' });
  };

  if (!appVars || !appVars.theme) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PasswordResetLoader message="Loading password update..." />
      </ThemeProvider>
    );
  }

  const logoSrc = appID === IMAGER_APP_ID ? 'https://imager.digitalwerks.com/public/brand/imager-logo.png' : `${appVars.api_url}/imageFiles/${appVars.appID}-logo.png`;
  const footerLabel = appVars.companyName || 'DigitalWerks';
  const loginHref = completedLoginUrl || passwordLoginUrl(appID, appVars);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const resetUnavailable = tokenState === 'missing' || tokenState === 'invalid';

  const resetStateContent = resetUnavailable ? (
    <Alert severity="error" variant="outlined" role="alert" sx={{ alignItems: 'flex-start' }}>
      <Typography component="h2" sx={{ fontWeight: 800, mb: 0.75 }}>
        {tokenState === 'missing' ? 'This reset link is incomplete' : 'This reset link is invalid or expired'}
      </Typography>
      <Typography>
        {tokenState === 'missing'
          ? 'This link is missing information needed to set your password. Request a fresh password email to continue.'
          : 'This one-time link may have expired or already been used. Request a new password-reset email to continue.'}
      </Typography>
      <Button component="a" href={`/password-reset/${appID}`} variant="contained" sx={{ mt: 2, textTransform: 'none' }}>
        Request a new reset link
      </Button>
    </Alert>
  ) : null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PasswordResetShell
        logoSrc={logoSrc}
        logoBackground={appID === IMAGER_APP_ID ? '#ffffff' : undefined}
        logoAlt={appVars.application}
        heroBadge="Protected reset link"
        heroEyebrow={appVars.application}
        heroTitle={appID === IMAGER_APP_ID ? 'Choose your password and open Imager.' : 'Choose a fresh password and get back in.'}
        heroDescription={`This page is tied to a secure password reset request for ${appVars.application}. Enter the new password you want to use, confirm it, and we’ll redirect you back to sign in.`}
        heroHighlights={[
          {
            label: 'Reset link',
            value: 'This screen is opened from a one-time password recovery email.',
          },
          {
            label: 'After submit',
            value: 'You will be sent back to the sign-in page once the password update succeeds.',
          },
        ]}
        //heroMediaSrc={heroImageSrc}
        heroMediaAlt={`${appVars.application} sign-in background`}
        heroMediaFallback="Your account password can be updated securely from this one-time screen."
        panelBadge={tokenState === 'completed' ? 'Password updated' : 'Create new password'}
        panelTitle={tokenState === 'completed' ? 'Your password is ready' : resetUnavailable ? 'Request a new secure link' : 'Set your new password'}
        panelDescription={tokenState === 'completed' ? 'Your previous sessions were closed. Sign in again with the new password.' : resetUnavailable ? 'Request a fresh email to choose your password securely.' : `Use at least ${PASSWORD_MIN_LENGTH} characters, then confirm the password exactly to finish the reset.`}
        footerHref={appVars.companyUrl || 'https://www.digitalwerks.com/'}
        footerLabel={footerLabel}
      >
        {resetStateContent}
        {tokenState === 'completed' ? (
          <Alert severity="success" variant="outlined" role="status">
            <Typography sx={{ fontWeight: 800, mb: 0.75 }}>Password updated successfully</Typography>
            <Typography>{appID === IMAGER_APP_ID ? 'Taking you to Imager. Sign in with the password you just created.' : 'Sign in with your new password to continue.'}</Typography>
            {loginHref ? <Button component="a" href={loginHref} variant="contained" sx={{ mt: 2, textTransform: 'none' }}>Return to sign in</Button> : null}
          </Alert>
        ) : null}
        {tokenState === 'ready' ? <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.25}>
            <TextField
              type="password"
              label="New password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              fullWidth
              autoComplete="new-password"
              required
              inputProps={{ minLength: PASSWORD_MIN_LENGTH, 'aria-describedby': 'password-guidance' }}
              disabled={submitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                },
              }}
            />
            <TextField
              type="password"
              label="Confirm new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              fullWidth
              autoComplete="new-password"
              required
              inputProps={{ minLength: PASSWORD_MIN_LENGTH, 'aria-describedby': 'password-guidance' }}
              disabled={submitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                },
              }}
            />
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 700 }}>Before you submit</Typography>
                <Typography id="password-guidance" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  Use at least {PASSWORD_MIN_LENGTH} characters, keep the password private, make sure both fields match, and avoid reusing a password from another system.
                </Typography>
                {confirmPassword ? (
                  <Typography
                    sx={{
                      color: passwordsMatch ? theme.palette.success.main : theme.palette.error.main,
                      fontWeight: 700,
                    }}
                  >
                    {passwordsMatch ? 'Passwords match and are ready to submit.' : 'Passwords do not match yet.'}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={submitting}
              startIcon={<Box component="img" src={SecurityLogo} alt="" sx={{ maxWidth: 28 }} />}
              sx={{
                py: 1.35,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
              }}
            >
              {submitting ? 'Updating password...' : 'Save new password'}
            </Button>
            {loginHref ? (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
                Need the sign-in page instead?{' '}
                <Link href={loginHref} underline="hover" sx={{ fontWeight: 700 }}>
                  Return to sign in
                </Link>
              </Typography>
            ) : null}
          </Stack>
        </Box> : null}
      </PasswordResetShell>

      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={error.show}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity={error.severity} variant="filled">
          {error.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default PasswordReset;
